import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { customerAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../ToastContext';
import RatingBadge from '../components/RatingBadge';
import MoneyInput from '../components/MoneyInput';
import { fmtDate } from '../utils/date';
import BackButton from '../components/BackButton';
import * as repo from '../sync/repo';

const askReason = (what) => {
  const r = window.prompt(`Why are you voiding this ${what}?`);
  return r === null ? null : (r.trim() || 'Voided');
};

export default function CustomerDetail() {
  const { id }       = useParams();
  const { isManager, readOnly, hasPermission } = useAuth();
  const canVoid = isManager && !readOnly;
  const { toast }    = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying]   = useState(false);
  const [payAmt, setPayAmt]   = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = () => customerAPI.getById(id).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const fmt = (n) => new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

  const handlePayment = async (e) => {
    e.preventDefault();
    const amt = Number(payAmt);
    if (!amt || amt <= 0) { toast('Enter a valid amount', 'error'); return; }
    setSubmitting(true);
    try {
      // The server refuses more than is owed (and says the maximum).
      const { data: res } = await customerAPI.addPayment(id, { amount: payAmt });
      toast(`Payment of ${fmt(res.applied)} applied — oldest debt first`, 'success');
      setPaying(false); setPayAmt('');
      load();
    } catch (err) {
      toast(err.response?.data?.error || 'Failed to record payment', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const voidSale = async (saleId) => {
    const reason = askReason('sale');
    if (reason === null) return;
    try {
      await repo.voidSale(saleId, reason);
      toast('Sale voided', 'success');
      load();
    } catch (err) { toast(err.response?.data?.error || 'Could not void the sale', 'error'); }
  };

  const voidPayment = async (paymentId) => {
    const reason = askReason('payment');
    if (reason === null) return;
    try {
      await repo.voidPayment(paymentId, reason);
      toast('Payment voided', 'success');
      load();
    } catch (err) { toast(err.response?.data?.error || 'Could not void the payment', 'error'); }
  };

  if (loading) return <div className="page"><p className="text-muted">Loading…</p></div>;
  if (!data?.customer) return <div className="page"><p className="text-muted">Customer not found.</p></div>;

  const { customer, sales, payments = [], outstanding } = data;
  const cur = customer.business_currency || '';

  // What was paid at the moment of each sale ('initial' payment rows).
  const paidAtSale = {};
  payments.filter(p => p.kind === 'initial').forEach(p => {
    paidAtSale[p.sale_id] = (paidAtSale[p.sale_id] || 0) + Number(p.amount);
  });

  // Combined history: sales (purchases) and later repayments, newest first.
  const timeline = [
    ...sales.map(s => ({
      kind: 'sale', key: `s-${s.id}`, id: s.id, date: s.sold_at,
      product_name: s.product_name, qty: s.qty,
      total_amount: s.total_amount, amount_paid: s.amount_paid,
      paid_at_sale: paidAtSale[s.id] || 0,
      payment_status: s.payment_status,
    })),
    ...payments.filter(p => p.kind !== 'initial').map(p => ({
      kind: 'payment', key: `p-${p.id}`, id: p.id, date: p.paid_at,
      product_name: p.product_name, amount: p.amount,
    })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div className="page">
      <BackButton />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
        <h1 className="page-title" style={{ margin: 0 }}>{customer.name}</h1>
        <RatingBadge rating={customer.rating} />
      </div>

      {customer.business_name && (
        <div style={{ marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '0.75rem', padding: '0.15rem 0.5rem', borderRadius: '4px',
            background: 'var(--color-primary-muted, #312e81)', color: 'var(--color-primary)' }}>
            {customer.business_name}
          </span>
        </div>
      )}

      {customer.contact && <p className="text-muted" style={{ marginBottom: '0.75rem' }}>{customer.contact}</p>}

      {Number(outstanding) > 0 && (hasPermission('manage_sales') && !readOnly) && (
        <div className="card" style={{ marginBottom: '1rem', borderColor: 'var(--color-warning)' }}>
          <div
            onClick={() => { setPaying(p => !p); setPayAmt(''); }}
            style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <div>
              <div className="text-muted" style={{ fontSize: '0.8rem' }}>Outstanding debt · tap to add payment</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--color-warning)' }}>{cur} {fmt(outstanding)}</div>
            </div>
            <button
              type="button"
              className="btn-primary"
              style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
              onClick={(e) => { e.stopPropagation(); setPaying(p => !p); setPayAmt(''); }}
            >
              {paying ? 'Close' : 'Add payment'}
            </button>
          </div>

          {paying && (
            <form onSubmit={handlePayment} style={{ marginTop: '0.75rem', borderTop: '1px solid var(--color-border)', paddingTop: '0.75rem' }}>
              <div className="form-group">
                <label>Amount received ({cur})</label>
                <MoneyInput value={payAmt} onChange={e => setPayAmt(e.target.value)} required autoFocus />
              </div>
              <div className="text-muted" style={{ fontSize: '0.75rem', marginBottom: '0.6rem' }}>
                Applied to the oldest debt first; any extra rolls over to the next debt. Up to {cur} {fmt(outstanding)}.
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button type="submit" className="btn-primary" disabled={submitting}>{submitting ? 'Saving…' : 'Record payment'}</button>
                <button type="button" className="btn-ghost" onClick={() => { setPaying(false); setPayAmt(''); }}>Cancel</button>
              </div>
            </form>
          )}
        </div>
      )}

      <h2 style={{ fontSize: '1rem', marginBottom: '0.5rem', color: 'var(--color-muted)' }}>History</h2>
      {timeline.length === 0 && <p className="text-muted">No transactions yet.</p>}
      {timeline.map(t => t.kind === 'sale' ? (
        <div key={t.key} className="card" style={{ marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
            <span style={{ fontWeight: 600 }}>{t.product_name}</span>
            <span className={`badge badge-${t.payment_status}`}>{t.payment_status}</span>
          </div>
          <div className="text-muted" style={{ fontSize: '0.85rem', marginTop: '0.2rem' }}>
            Bought · {cur} {fmt(t.total_amount)} · Qty {t.qty} · {fmtDate(t.date)}
          </div>
          <div className="text-muted" style={{ fontSize: '0.78rem' }}>Paid {cur} {fmt(t.paid_at_sale)} at time of sale</div>
          {(t.payment_status === 'partial' || t.payment_status === 'debt') && (
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--color-warning)' }}>
              {cur} {fmt(Number(t.total_amount) - Number(t.amount_paid))} still outstanding
            </div>
          )}
          {canVoid && (
            <div style={{ textAlign: 'right' }}>
              <button className="btn-ghost" style={{ padding: '0.2rem 0.6rem', fontSize: '0.72rem' }} onClick={() => voidSale(t.id)}>Void sale</button>
            </div>
          )}
        </div>
      ) : (
        <div key={t.key} className="card" style={{ marginBottom: '0.5rem', borderLeft: '3px solid var(--color-success, #22c55e)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
            <span style={{ fontWeight: 600, color: 'var(--color-success, #22c55e)' }}>Payment received</span>
            <span style={{ fontWeight: 700, color: 'var(--color-success, #22c55e)' }}>+{cur} {fmt(t.amount)}</span>
          </div>
          <div className="text-muted" style={{ fontSize: '0.85rem', marginTop: '0.2rem' }}>
            Toward {t.product_name} · {fmtDate(t.date)}
          </div>
          {canVoid && (
            <div style={{ textAlign: 'right' }}>
              <button className="btn-ghost" style={{ padding: '0.2rem 0.6rem', fontSize: '0.72rem' }} onClick={() => voidPayment(t.id)}>Void payment</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
